import React, { useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { Box } from '@iris/react-components';
import clsx from 'clsx';

import { formatCurrency } from '@atc/string-fns';
import { calculatePersonalPayment } from '@atc/wallet-payment-fns';

import { Button, Text } from 'reaxl';
import { Pricing } from 'reaxl-molecules';

import { defaultPaymentInfo } from 'axl-config';

import { getPriceLabel } from 'wallet-manager';

import paymentsDuck from '@/ducks/paymentsDuck';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import usePaymentData from '@/hooks/usePaymentData';

import PricingBreakdown from '@/components/PricingBreakdown';
import PricingIndicator from '@/components/PricingIndicator';

function PricingCard({
    includeTaxesFees,
    handlePriceAndPaymentsClick = () => { },
    includeFinancePaymentOfferToWalletCTA = false,
    uiContext = '',
}) {

    const { pricingDetail = {}, makeCode, type, id: listingId, paymentServices: { digitalRetailingType = '', dealerSettings = {} } = {} } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const listingIncentives = useSelector(paymentsDuck.selectors.getListingIncentives) || {};
    const [isLoading, hasError, paymentsData] = usePaymentData();
    const [financeOffersObj, setFinanceOffersObj] = useState({});

    // Get applied incentives for the listing for price breakdown
    const appliedIncentives = listingIncentives?.[listingId]?.finance.filter((incentive) => financeOffersObj?.appliedIncentiveIds?.includes(incentive.id)) || [];
    const enableOfferBreakdownIncentives = digitalRetailingType !== 'psx' && dealerSettings?.priceBreakdownEnabled;

    // Determine price label based on vehicle make and type
    const priceLabel = getPriceLabel({ makeCode, type });

    useEffect(() => {
        if (paymentsData) {
            paymentsData.map((item) => {
                if (item.paymentLabel?.toLowerCase() === 'finance') {
                    setFinanceOffersObj(item);
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(paymentsData)]);

    const financePayment = useMemo(() => {
        const { payment, pricingDetails, customRate } = financeOffersObj;
        if (!customRate) {
            return payment;
        }

        return Math.max(0, calculatePersonalPayment(pricingDetails.salePrice, financeOffersObj || defaultPaymentInfo, customRate)) || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(financeOffersObj)]);

    const walletCTA = (includeFinancePaymentOfferToWalletCTA && !isLoading && !hasError && financePayment > 0) ? (
        <div className="margin-left-3 text-normal">
            <div className="display-flex flex-wrap text-size-200">
                <span className="margin-right-1">
                    <Text
                        size={200}
                        weight="bold"
                    >
                        {formatCurrency(financePayment)}
                    </Text>
                    /mo.
                    {includeTaxesFees && <span>*</span>}
                </span>
                <Button
                    bsStyle="text"
                    onClick={handlePriceAndPaymentsClick}
                >
                    See finance details
                </Button>
            </div>
            {includeTaxesFees && (
                <Text
                    size={100}
                    color="gray-dark"
                    componentClass="div"
                >
                    * includes est. taxes and fees
                </Text>
            )}
        </div>
    ) : (
        <Button
            bsStyle="text"
            className="display-block margin-left-3 text-size-200 text-normal"
            onClick={handlePriceAndPaymentsClick}
        >
            See price and payment details
        </Button>
    );

    if (uiContext === 'price-section') {
        return (
            <Box
                as="div"
                className="iris:w-full"
            >
                <PricingBreakdown
                    data={pricingDetail}
                    uiContext="price-section"
                    financeOffersObj={financeOffersObj}
                    appliedIncentives={appliedIncentives}
                    enableOfferBreakdownIncentives={enableOfferBreakdownIncentives}
                />
                <PricingIndicator
                    pricingDetail={pricingDetail}
                />
            </Box>
        );
    }

    return (
        <div
            className={clsx({
                'margin-bottom-3': includeFinancePaymentOfferToWalletCTA,
                'margin-bottom-2': !includeFinancePaymentOfferToWalletCTA,
            })}
        >
            <PricingIndicator
                pricingDetail={pricingDetail}
                isInline
            />
            <Pricing
                uiContext="withDealIndicator"
                showSavings
                data={pricingDetail}
                walletCTA={walletCTA}
                priceLabel={priceLabel}
            />
        </div>
    );
}

PricingCard.propTypes = {
    includeTaxesFees: PropTypes.bool,
    /**
     * Handler for price and payments button click
     */
    handlePriceAndPaymentsClick: PropTypes.func,
    /**
     * Include Finance Payment Offer to Wallet CTA
     */
    includeFinancePaymentOfferToWalletCTA: PropTypes.bool,
};

export default PricingCard;
